import { useCallback, useMemo, useState } from 'react';
import { useSdkQuery } from '@/provider';
import { useControlledState } from '@/hooks/use-controlled-state';
import { FlashcardController, FlashcardDeckWidgetData } from '../types';

export type FlashcardSelector = (
  data: FlashcardDeckWidgetData[]
) => FlashcardDeckWidgetData | null;

export type UseFlashcardControllerProps = {
  reference: string;
  displayIndex?: number;
  preventFlipBack?: boolean;
  disabled?: boolean;
  onFlip?: (index: number, side: 'front' | 'back') => void;
  onNavigateTo?: (index: number) => void;
  onLoad?: (data: FlashcardDeckWidgetData[]) => void;
  selectDeck?: (
    data: FlashcardDeckWidgetData[]
  ) => FlashcardDeckWidgetData | null;
};

export function useFlashcardController(
  props: UseFlashcardControllerProps
): FlashcardController {
  const {
    reference,
    displayIndex,
    preventFlipBack,
    disabled,
    onFlip = () => {},
    onNavigateTo = () => {},
    onLoad = () => {},
    selectDeck = (decks) => decks[0],
  } = props;

  const [flipState, setFlipState] = useState<Record<number, boolean>>({});

  const [currentIndex, setCurrentIndex] = useControlledState({
    value: displayIndex,
    defaultValue: 0,
    onChange: (value) => {
      onNavigateTo(value);
    },
  });

  const currentSide = flipState[currentIndex] ? 'back' : 'front';

  // const [visited, setVisited] = useState<number[]>([]);

  const decks = useSdkQuery(
    `flashcard-decks-${reference}`,
    async (sdk) => {
      return sdk.flashcard.getDeckByReference(reference);
    },
    {
      onSuccess: (data) => {
        setCurrentIndex(0);
        setFlipState({});
        onLoad(data);
      },
    }
  );

  const currentDeck = useMemo(
    () => (!decks.data ? null : selectDeck(decks.data)),
    [selectDeck, decks.data]
  );

  const currentFlashcard = currentDeck?.content[currentIndex] || null;

  const handleFlip = useCallback(
    (index?: number) => {
      const flipIndex = index || currentIndex;
      const isFlipped = flipState[index || currentIndex];

      // skip if flip back is prevented
      if (disabled || (isFlipped && preventFlipBack)) {
        return;
      }

      setFlipState((v) => ({
        ...v,
        [flipIndex]: !v[flipIndex],
      }));

      onFlip(flipIndex, isFlipped ? 'front' : 'back');
    },
    [onFlip, currentIndex, preventFlipBack, disabled, flipState]
  );

  const handleNavigateTo = useCallback(
    (index: number) => {
      if (disabled || !currentDeck) return;

      if (index >= 0 && index < currentDeck.content.length) {
        setCurrentIndex(index);
      }
    },
    [disabled, currentDeck, setCurrentIndex]
  );

  const flipAll = useCallback(
    (side: 'front' | 'back') => {
      if (!currentDeck) return;

      setFlipState(
        currentDeck.content.reduce<Record<number, boolean>>(
          (carry, _, index) => {
            carry[index] = side !== 'front';
            return carry;
          },
          {}
        )
      );
    },
    [currentDeck]
  );

  return {
    deck: currentDeck || null,
    current: currentFlashcard,
    // visited,
    loading: decks.loading,
    disabled: Boolean(disabled),
    preventFlipBack: Boolean(preventFlipBack),
    displayIndex: currentIndex,
    displaySide: currentSide,
    navigateTo: handleNavigateTo,
    flip: handleFlip,
    flipSide: (index: number) => (flipState[index] ? 'back' : 'front'),
    flipAll,
  };
}
